import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import apiClient from "../services/apiClient"; // Import apiClient
import CheckoutForm from "../components/CheckoutForm"; // Import CheckoutForm
import pricingData from "../data/pricing_config.json"; // Import pricing config
import { useQuery } from "@tanstack/react-query";
import {
  FaArrowRight,
  FaStar,
  FaGem,
  FaSpinner,
  FaCheckCircle, // Import FaCheckCircle
} from "react-icons/fa"; // Import icons
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"; // Import useLocation and useNavigate
import { useAuthStore } from "../store/authStore";
// Define CurrencyCode and TargetPrices types
type CurrencyCode =
  | "THB"
  | "MXN"
  | "BRL"
  | "USD"
  | "EUR"
  | "GBP"
  | "PLN"
  | "NOK"
  | "SEK"
  | "INR"
  | "JPY"
  | "KRW"
  | "IDR"
  | "VND";

type TargetPrices = {
  [key in CurrencyCode]?: number;
};

// Optional: Define the structure of the imported pricing data
interface PricingPackageConfig {
  logicalKey: string;
  name: string;
  credits: number;
  targetPrices: TargetPrices;
}

// Load Stripe publishable key from environment variables
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ""
);

// Interface for localized package pricing data
interface PackagePricing {
  logicalKey: string;
  credits: number;
  currency: string;
  price: number | null;
  formattedPrice: string | null;
  available: boolean;
}

interface LocalizedPricing {
  currency: string;
  packages: PackagePricing[];
}

const PurchasePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate(); // Get navigate function
  const [selectedPackageKey, setSelectedPackageKey] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(false); // Global loading state for API call
  const [processingPackageKey, setProcessingPackageKey] = useState<
    string | null
  >(null); // Track which package is processing
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentCurrency, setPaymentCurrency] = useState<string | null>(null); // State for currency
  const [displayPrice, setDisplayPrice] = useState<string | null>(null); // State for display price

  const location = useLocation(); // Get location object
  const redirectMessageKey = location.state?.messageKey; // Get the message key from state
  const { user } = useAuthStore();

  if (user?.is_anonymous) {
    navigate("/login", {
      state: { messageKey: "insufficient_credits_redirect" },
    });
  }

  // Function to track purchase event
  const trackPurchaseEvent = (
    purchasedPackageKey: string | null,
    paymentCurrency: string | null
  ) => {
    try {
      if (!purchasedPackageKey || !paymentCurrency) {
        console.warn("Missing data for purchase tracking.");
        return;
      }
      // Find the purchased package config to get the target price
      const purchasedPackageConfig = pricingData.find(
        (p: PricingPackageConfig) => p.logicalKey === purchasedPackageKey
      );

      // Safely access the price using the defined types
      let value: number | undefined = undefined;
      if (
        purchasedPackageConfig &&
        paymentCurrency &&
        purchasedPackageConfig.targetPrices
      ) {
        // Ensure paymentCurrency is a valid CurrencyCode before accessing
        const currencyKey = paymentCurrency as CurrencyCode;
        if (currencyKey in purchasedPackageConfig.targetPrices) {
          value = purchasedPackageConfig.targetPrices[currencyKey];
        }
      }

      // Ensure values conform to Record<string, string | number>
      const finalValue = value === undefined ? 0 : value; // Default to 0 if undefined

      window.trackEvent("Purchase", {
        package: purchasedPackageKey as string, // Assert as string
        currency: paymentCurrency as string, // Assert as string
        value: finalValue, // Use the guaranteed number
      });
    } catch (error) {
      console.error("Error tracking purchase event:", error);
    }
  };

  const { data: localizedPrices, isLoading: loadingPrices } =
    useQuery<LocalizedPricing>({
      queryKey: ["localizedPrices"],
      queryFn: async () => {
        const response = await apiClient.get<LocalizedPricing>(
          "/packages/prices",
          {
            params: {
              test_currency: testCurrency,
            },
          }
        );
        if (!response.data) {
          throw new Error("No pricing data received");
        }
        return response.data;
      },
    });

  // Hardcoded features mapped by logicalKey (could move to JSON later)
  const packageFeatures: { [key: string]: string[] } = {
    basic: [
      t("purchasePage.features.basicImages", "10 images"),
      t("purchasePage.features.standardRes", "Standard resolution"),
    ],
    standard: [
      t("purchasePage.features.standardImages", "50 images"),
      t("purchasePage.features.highRes", "High resolution options"),
    ],
    premium: [
      t("purchasePage.features.premiumImages", "100 images"),
      t("purchasePage.features.allRes", "All resolution options"),
    ],
  };

  // Add popularity flag mapping (could also move to JSON)
  const popularPackages: { [key: string]: boolean } = {
    standard: true,
  };

  // Add CTA mapping (could also move to JSON)
  const packageCtas: { [key: string]: string } = {
    basic: t("purchasePage.cta.selectBasic", "Get Started"),
    standard: t("purchasePage.cta.selectStandard", "Choose Standard"),
    premium: t("purchasePage.cta.selectPremium", "Go Premium"),
  };

  const [searchParams] = useSearchParams();
  const testCurrency = searchParams.get("test_currency");

  const handlePurchase = async (logicalKey: string) => {
    // Now accepts logicalKey directly
    if (!logicalKey) {
      // Basic check, should always have a key here
      setError(t("purchasePage.errors.selectPackage"));
      return;
    }
    setLoading(true);
    setSelectedPackageKey(logicalKey); // Set the selected key for payment processing
    setProcessingPackageKey(logicalKey); // Indicate which package button was clicked
    setError(null);
    setClientSecret(null); // Reset payment state before fetching new one
    setPaymentCurrency(null);
    setDisplayPrice(null);

    window.trackEvent?.("AddToCart", {
      package: logicalKey,
    });

    try {
      const response = await apiClient.post<{
        clientSecret: string;
        currency: string;
        displayPrice: string; // This is the actual price confirmed by backend
      }>("/payment-sheet", {
        package: logicalKey, // Send the logicalKey from the argument
        test_currency: testCurrency,
      });

      if (
        response.data &&
        response.data.clientSecret &&
        response.data.currency &&
        response.data.displayPrice // Check for the backend-confirmed price
      ) {
        setClientSecret(response.data.clientSecret);
        setPaymentCurrency(response.data.currency.toUpperCase());
        setDisplayPrice(response.data.displayPrice); // Store backend-confirmed display price
      } else {
        throw new Error("Invalid response from payment-sheet endpoint");
      }
    } catch (err) {
      console.error("Failed to create payment sheet:", err);
      setError(t("purchasePage.errors.paymentSheetFailed"));
      setSelectedPackageKey(null); // Clear selection on error if we don't proceed
    } finally {
      setLoading(false);
      setProcessingPackageKey(null); // Stop indicating processing for this specific package
    }
  };

  // Options for the Elements provider based on clientSecret
  const options = clientSecret
    ? { clientSecret, appearance: { theme: "night" as const } }
    : undefined;

  return (
    // The main layout provides the container, padding, etc.
    // We just render the specific content for this page.
    <div className="max-w-5xl mx-auto p-8 rounded-lg shadow-xl text-white">
      <h1 className="text-3xl font-bold text-center mb-6 text-white">
        {t("purchasePage.title", "Buy Credits")}
      </h1>

      {/* Display message if redirected due to insufficient credits */}
      {redirectMessageKey === "insufficient_credits_redirect" && (
        <div className="mb-6 p-4 bg-yellow-900 bg-opacity-60 text-yellow-200 rounded-lg border border-yellow-700 text-center">
          <p className="font-semibold">
            {t(
              "purchasePage.insufficientCreditsRedirectMessage",
              "You need more credits to process your image. Please select a package below."
            )}
          </p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-3 bg-red-900 bg-opacity-50 text-red-200 rounded-md border border-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Package Selection (Only show if not proceeding to payment) */}
      {!clientSecret && (
        <>
          {/* Centralized Loading Indicator for Prices */}
          {loadingPrices && (
            <div className="flex justify-center items-center mb-8">
              <FaSpinner className="animate-spin text-yellow-400 text-2xl mr-3" />
              <span className="text-gray-300">
                {t("purchasePage.loadingPrices", "Loading prices...")}
              </span>
            </div>
          )}

          {/* Hide grid until prices are loaded */}
          {!loadingPrices && localizedPrices && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {pricingData.map((pkg) => {
                // Use data from imported pricingData
                // isSelected is now mainly for potential styling during processing
                const isSelected = selectedPackageKey === pkg.logicalKey;
                const isProcessing = processingPackageKey === pkg.logicalKey;
                const features = packageFeatures[pkg.logicalKey] || [];
                const isPopular = popularPackages[pkg.logicalKey] || false;
                const ctaText =
                  packageCtas[pkg.logicalKey] ||
                  t("purchasePage.cta.select", "Select");

                // Define icons for each package
                const packageIcons: { [key: string]: React.ElementType } = {
                  basic: FaArrowRight,
                  standard: FaStar,
                  premium: FaGem,
                };
                const IconComponent =
                  packageIcons[pkg.logicalKey] || FaArrowRight; // Fallback icon

                // Find localized price information if available
                const localizedPkg = localizedPrices?.packages.find(
                  (p) => p.logicalKey === pkg.logicalKey
                );

                return (
                  <div
                    key={pkg.logicalKey}
                    className={`magical-glass-card relative flex flex-col p-6 transition-all duration-200 ${
                      isSelected ? "scale-105 shadow-lg border-yellow-500" : ""
                    } ${isPopular ? "border-yellow-600" : ""}`}
                  >
                    {isPopular && (
                      <span className="absolute top-0 right-0 bg-yellow-500 text-black text-xs font-semibold px-3 py-1 rounded-bl-lg rounded-tr-md">
                        {t("purchasePage.popular", "Popular")}
                      </span>
                    )}
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {/* Use name from JSON, provide fallback if needed */}
                      {t(
                        `purchasePage.packages.${pkg.logicalKey}.name`,
                        pkg.name
                      )}
                    </h3>
                    <p className="text-2xl font-bold text-yellow-400 mb-2">
                      {t("purchasePage.creditsCount", { count: pkg.credits })}
                    </p>
                    {/* Display localized price or unavailable message */}
                    <p className="text-sm text-gray-300 mb-4 h-5">
                      {" "}
                      {/* Added fixed height h-5 */}
                      {localizedPkg?.formattedPrice
                        ? localizedPkg.formattedPrice
                        : t(
                            "purchasePage.priceUnavailable",
                            "Price unavailable"
                          )}
                    </p>
                    <ul className="flex-grow space-y-2 text-sm text-gray-400 mb-6">
                      {features.map((feature, index) => (
                        <li key={index} className="flex items-center">
                          {/* Use FaCheckCircle Icon */}
                          <FaCheckCircle className="w-4 h-4 mr-2 text-green-500 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handlePurchase(pkg.logicalKey)} // Call handlePurchase directly
                      disabled={loading || !localizedPkg?.available} // Disable if loading globally OR if package is unavailable
                      className={`w-full py-2.5 px-4 rounded-md font-semibold text-center transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                        // Removed focus styles and shadow
                        // Shiny gradient for active, high-contrast flat for disabled/loading
                        loading || !localizedPkg?.available // Check availability for styling too
                          ? "bg-gray-700 text-gray-400 cursor-not-allowed" // Removed focus:ring-gray-500
                          : "bg-gradient-to-b from-yellow-500 to-yellow-600 text-black" // Removed focus:ring-yellow-500
                      }`}
                    >
                      {isProcessing ? ( // Show processing icon and text
                        <>
                          <FaSpinner className="animate-spin mr-2" />
                          {t("purchasePage.processingButton", "Processing...")}
                        </>
                      ) : !localizedPkg?.available ? ( // Show unavailable text if applicable
                        t("purchasePage.cta.unavailable", "Unavailable")
                      ) : (
                        // Show package icon and CTA text
                        <>
                          <IconComponent className="mr-2" />
                          {ctaText}
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Payment Section (Stripe Elements) */}
      {clientSecret && paymentCurrency && displayPrice && selectedPackageKey ? ( // Added selectedPackageKey check for safety
        <div className="mt-8 p-6 bg-gray-800 border border-gray-700 rounded-lg shadow-md">
          <div className="flex justify-between items-center mb-4 text-white">
            {t(
              "purchasePage.confirmPurchase",
              "Confirm purchase of {{packageName}} for {{price}}",
              {
                packageName:
                  pricingData.find((p) => p.logicalKey === selectedPackageKey)
                    ?.name || selectedPackageKey, // Fallback to key
                price: displayPrice, // Use the price confirmed by the backend
              }
            )}
          </div>
          <Elements stripe={stripePromise} options={options}>
            <CheckoutForm
              onSuccess={() => {
                // Clear local state related to the current payment attempt
                const purchasedPackageKey = selectedPackageKey; // Store key before clearing
                setClientSecret(null);
                setSelectedPackageKey(null);
                setPaymentCurrency(null);
                setDisplayPrice(null);
                setError(null); // Clear any previous errors

                // Track the purchase event
                trackPurchaseEvent(purchasedPackageKey, paymentCurrency);

                // Redirect to success/pending page instead of alert
                // Pass the key of the package being purchased
                if (purchasedPackageKey) {
                  navigate("/purchase/success");
                } else {
                  // Fallback if key is somehow lost, maybe go to dashboard?
                  console.warn(
                    "Package key lost after successful payment initiation. Redirecting to dashboard."
                  );
                  navigate("/dashboard"); // Or some other sensible default
                }
              }}
              onError={(errorMessage: string) => {
                setError(errorMessage); // Show specific Stripe error
                setClientSecret(null);
                setSelectedPackageKey(null); // Clear package selection forcing user back
                setPaymentCurrency(null);
                setDisplayPrice(null);
              }}
            />
          </Elements>
        </div>
      ) : null}
    </div>
  );
};

export default PurchasePage;
